import { useEffect, useRef, useState } from "react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDTypography from "components/MDTypography"
import SectionCard from "../SectionCard"
import { Button } from "../../ui"
import ActionNotes, { collectActionNotes } from "../ActionNotes"
import { DeadlineField, useDeadlineFields, buildDeadlineParam } from "../DeadlineField"
import { formatVND } from "../../constants"
import OrderLinePriceChanges from "./OrderLinePriceChanges"

// Right-column action surface for the Purchase (PO) Detail screen. Buttons are
// driven ENTIRELY by `allowedActions` from the backend — never inferred from
// status. The card is controlled: it reports the picked action + note up via
// `onAct(actionKey, note)`; the container owns the actual mutation dispatch and
// the refetch afterwards.

// Covers the full PO lifecycle. `noteRequired` mirrors the BE contract:
// reject / info_request / resubmit / cancel carry a reason, submit / approve /
// issue don't.
export const PO_ACTION_META = {
  submit: {
    label: "Submit for Approval",
    color: "info",
    variant: "gradient",
    icon: "send",
    title: "Submit this PO for approval",
    confirm: "Submit",
    noteRequired: false,
    placeholder: "Add a note (optional)",
  },
  approve: {
    label: "Approve",
    color: "success",
    variant: "gradient",
    icon: "check_circle",
    title: "Approve this PO",
    confirm: "Approve",
    noteRequired: false,
    placeholder: "Add a note (optional)",
  },
  reject: {
    label: "Reject",
    color: "error",
    variant: "outlined",
    icon: "cancel",
    title: "Reject this PO",
    confirm: "Reject",
    noteRequired: true,
    placeholder: "Reason for rejection",
  },
  info_request: {
    label: "Request Info",
    color: "warning",
    variant: "outlined",
    icon: "help",
    title: "Request more information",
    confirm: "Send request",
    noteRequired: true,
    placeholder: "What do you need before approving?",
  },
  resubmit: {
    label: "Resubmit",
    color: "info",
    variant: "gradient",
    icon: "replay",
    title: "Resubmit this PO",
    confirm: "Resubmit",
    noteRequired: true,
    placeholder: "Describe what changed / the info provided",
  },
  issue: {
    label: "Issue PO",
    color: "success",
    variant: "gradient",
    icon: "local_shipping",
    title: "Issue this PO",
    confirm: "Issue",
    noteRequired: false,
    placeholder: "Add a note (optional)",
  },
  receive: {
    label: "Receive Goods",
    color: "success",
    variant: "gradient",
    icon: "inventory_2",
    title: "Record a goods receipt",
    confirm: "Save",
    noteRequired: false,
    placeholder: "Add a note (optional)",
  },
  send_to_vendor: {
    label: "Send to Vendor",
    color: "info",
    variant: "outlined",
    icon: "forward_to_inbox",
    title: "Email this PO to the supplier",
    confirm: "Send",
    noteRequired: false,
    placeholder: "",
  },
  resend_to_vendor: {
    label: "Resend to Vendor",
    color: "info",
    variant: "outlined",
    icon: "forward_to_inbox",
    title: "Email this PO to the supplier again",
    confirm: "Resend",
    noteRequired: false,
    placeholder: "",
  },
  cancel: {
    label: "Cancel PO",
    color: "error",
    variant: "outlined",
    icon: "block",
    title: "Cancel this PO",
    confirm: "Cancel PO",
    noteRequired: true,
    placeholder: "Reason for cancellation",
  },
}

// Outlined (non-primary) actions keep their meaning through a tinted outline:
// destructive = red, request-info = amber; everything else is a plain secondary.
const ACTION_TONE_CLASS = { error: "pc-btn--danger-outline", warning: "pc-btn--warn-outline" }

// Stable button order so the surface reads the same regardless of the order the
// backend lists actions in.
export const PO_ACTION_ORDER = [
  "submit",
  "resubmit",
  "approve",
  "reject",
  "info_request",
  "issue",
  "receive",
  "send_to_vendor",
  "resend_to_vendor",
  "cancel",
]

export const allowedPoActions = (actions = []) =>
  PO_ACTION_ORDER.filter((a) => actions.includes(a))

// `guard(actionKey)` → false (or a Promise of false) stops the action before its modal opens.
export const PurchaseActionBar = ({ allowedActions = [], po, busy = false, onAct, guard }) => {
  const actions = allowedPoActions(allowedActions)
  const [activeAction, setActiveAction] = useState(null)

  return (
    <SectionCard title="Actions" subtitle="Available actions for this PO" icon="gavel" mb={0}>
      {actions.length > 0 ? (
        <MDBox display="flex" flexDirection="column" gap={1}>
          {actions.map((key) => {
            const meta = PO_ACTION_META[key]
            if (!meta) return null
            return (
              <Button
                key={key}
                block
                variant={meta.variant === "gradient" ? "primary" : "secondary"}
                className={ACTION_TONE_CLASS[meta.variant === "outlined" ? meta.color : ""] || ""}
                leftIcon={<Icon>{meta.icon}</Icon>}
                disabled={busy}
                onClick={async () => (!guard || (await guard(key))) && setActiveAction(key)}
              >
                {meta.label}
              </Button>
            )
          })}
        </MDBox>
      ) : (
        <MDBox textAlign="center" py={1}>
          <MDBox
            width="2.75rem"
            height="2.75rem"
            borderRadius="50%"
            bgColor="grey-100"
            color="text"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            mb={1.5}
          >
            <Icon>check_circle</Icon>
          </MDBox>
          <MDTypography variant="body2" color="text">
            There&apos;s nothing for you to act on right now.
          </MDTypography>
        </MDBox>
      )}

      {activeAction === "receive" && (
        <ReceiveGoodsModal
          po={po}
          busy={busy}
          onCancel={() => !busy && setActiveAction(null)}
          onSubmit={async (payload) => {
            const ok = await onAct("receive", payload)
            if (ok) setActiveAction(null)
          }}
        />
      )}

      {activeAction && activeAction !== "receive" && (
        <PurchaseConfirmModal
          actionKey={activeAction}
          po={po}
          busy={busy}
          onCancel={() => !busy && setActiveAction(null)}
          onSubmit={async (note, deadline) => {
            const ok = await onAct(activeAction, note, deadline)
            if (ok) setActiveAction(null)
          }}
        />
      )}
      <ActionNotes notes={collectActionNotes(po?.actionLog, po?.approvalProgress)} />
    </SectionCard>
  )
}

// Confirmation dialog for a single picked action. `busy` blocks double-submit.
export const PurchaseConfirmModal = ({ actionKey, po, busy, onCancel, onSubmit }) => {
  const meta = PO_ACTION_META[actionKey]
  const [note, setNote] = useState("")
  const { date, setDate, time, setTime } = useDeadlineFields(actionKey)

  useEffect(() => setNote(""), [actionKey])

  if (!meta) return null
  const invalid = meta.noteRequired && !note.trim()
  const isVendorMail = actionKey === "send_to_vendor" || actionKey === "resend_to_vendor"

  return (
    <Dialog open onClose={() => !busy && onCancel()} fullWidth maxWidth="sm">
      <DialogTitle>{meta.title}</DialogTitle>
      <DialogContent>
        <MDBox pt={1} display="flex" flexDirection="column" gap={2}>
          {(actionKey === "approve" || actionKey === "issue") && (
            <MDBox p={1.5} borderRadius="lg" bgColor="grey-100">
              <MDTypography variant="button" color="text">
                {actionKey === "issue" ? "Issuing" : "Approving"}{" "}
                <strong>{formatVND(po?.total)}</strong>
                {po?.poCode ? ` for ${po.poCode}` : ""}.
              </MDTypography>
            </MDBox>
          )}
          {actionKey === "approve" && <OrderLinePriceChanges purchaseId={po?.id} />}
          {isVendorMail ? (
            <MDTypography variant="body2" color="text">
              The PO details (items, prices, total, delivery points) will be emailed to{" "}
              <strong>{po?.supplierName || "the supplier"}</strong>, cc the PO creator.
            </MDTypography>
          ) : (
            <MDInput
              fullWidth
              multiline
              rows={4}
              labelType="non-sticky"
              labelText="Note"
              required={meta.noteRequired}
              placeholder={meta.placeholder}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
            />
          )}
          {actionKey === "info_request" && (
            <DeadlineField
              date={date}
              time={time}
              onChangeDate={setDate}
              onChangeTime={setTime}
              disabled={busy}
            />
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color={meta.color}
          onClick={() => onSubmit(note.trim(), buildDeadlineParam(date, time))}
          disabled={busy || invalid}
        >
          {busy ? "Submitting…" : meta.confirm}
        </MDButton>
      </DialogActions>
    </Dialog>
  )
}

// Goods-receipt modal: optional file (the receipt/GRN photo or PDF) + note +
// a "fully received" toggle. Separate from PurchaseConfirmModal because
// `receive` is the only PO action that uploads a file (multipart, not a plain
// { action, note } body) — see receivePurchase in procurementPurchases.js.
export const ReceiveGoodsModal = ({ po, busy, onCancel, onSubmit }) => {
  const [note, setNote] = useState("")
  const [file, setFile] = useState(null)
  const [complete, setComplete] = useState(false)
  const [notifyVendor, setNotifyVendor] = useState(false)
  const [notifyProManager, setNotifyProManager] = useState(false)
  const fileInputRef = useRef(null)

  return (
    <Dialog open onClose={() => !busy && onCancel()} fullWidth maxWidth="sm">
      <DialogTitle>Record a goods receipt{po?.poCode ? ` — ${po.poCode}` : ""}</DialogTitle>
      <DialogContent>
        <MDBox pt={1} display="flex" flexDirection="column" gap={2}>
          <MDBox>
            <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
              Receipt document (photo / PDF, optional)
            </MDTypography>
            {file ? (
              <MDBox display="flex" alignItems="center" justifyContent="space-between" p={1} borderRadius="md" bgColor="grey-100">
                <MDTypography variant="caption">{file.name}</MDTypography>
                <MDButton size="small" variant="text" color="error" onClick={() => setFile(null)} disabled={busy}>
                  <Icon fontSize="small">close</Icon>
                </MDButton>
              </MDBox>
            ) : (
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                <Icon sx={{ mr: 0.5 }}>attach_file</Icon>
                Attach file
              </MDButton>
            )}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) setFile(f)
                e.target.value = ""
              }}
            />
          </MDBox>
          <MDInput
            fullWidth
            multiline
            rows={3}
            labelType="non-sticky"
            labelText="Note"
            placeholder="What was received (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
          />
          <FormControlLabel
            control={<Checkbox checked={complete} onChange={(e) => setComplete(e.target.checked)} disabled={busy} />}
            label="Fully received — close out goods receipt for this PO"
          />
          {complete && po?.internalTransfer && (
            <MDBox p={1.5} borderRadius="lg" bgColor="grey-100">
              <MDTypography variant="caption" color="text">
                Received qty goes to each center; the rest returns to{" "}
                <strong>{po.internalStock?.warehouseName || "the warehouse"}</strong>. Whole numbers only.
              </MDTypography>
            </MDBox>
          )}
          {complete && (
            <MDBox display="flex" flexDirection="column">
              <MDTypography variant="caption" color="text" mb={0.5}>
                The order requester is always notified. Also notify:
              </MDTypography>
              <FormControlLabel
                control={<Checkbox checked={notifyVendor} onChange={(e) => setNotifyVendor(e.target.checked)} disabled={busy} />}
                label="Supplier"
              />
              <FormControlLabel
                control={<Checkbox checked={notifyProManager} onChange={(e) => setNotifyProManager(e.target.checked)} disabled={busy} />}
                label="Procurement managers"
              />
            </MDBox>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="success"
          onClick={() =>
            onSubmit({
              file,
              note: note.trim(),
              complete,
              notifyTargets: [notifyVendor && "vendor", notifyProManager && "pro_manager"].filter(Boolean).join(",") || undefined,
            })
          }
          disabled={busy}
        >
          {busy ? "Saving…" : "Save"}
        </MDButton>
      </DialogActions>
    </Dialog>
  )
}

export default PurchaseActionBar
